import React, { useState, useEffect } from 'react';
import { Bell, X, Smartphone, Share, Loader2 } from 'lucide-react';

/**
 * Componente per richiedere l'attivazione delle notifiche push
 * Mostra un banner dopo il primo login se le notifiche non sono attive
 * Su iOS mostra le istruzioni per installare la PWA
 */
const NotificationPrompt = ({ show, onClose, tenantId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Rileva iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Rileva se già installato come PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
  }, []);

  useEffect(() => {
    // Mostra solo se:
    // 1. Il prop show è true
    // 2. Non è già stato mostrato in questa sessione
    // 3. Le notifiche non sono già attive
    const checkAndShow = async () => {
      const alreadyShown = sessionStorage.getItem('notification_prompt_shown');
      const alreadyDismissed = localStorage.getItem('notification_prompt_dismissed');
      
      if (show && !alreadyShown && !alreadyDismissed) {
        // Su iOS: mostra sempre se non è PWA installata
        if (isIOS && !isStandalone) {
          setIsVisible(true);
          sessionStorage.setItem('notification_prompt_shown', 'true');
          return;
        }
        
        // Su altri browser: verifica se le notifiche sono già attive
        if (window.checkNotificationStatus) {
          const status = await window.checkNotificationStatus();
          if (!status) {
            setIsVisible(true);
            sessionStorage.setItem('notification_prompt_shown', 'true');
          }
        } else {
          // OneSignal non ancora caricato, mostra comunque
          setTimeout(() => {
            setIsVisible(true);
            sessionStorage.setItem('notification_prompt_shown', 'true');
          }, 2000);
        }
      }
    };
    
    checkAndShow();
  }, [show, isIOS, isStandalone]);

  const [errorMsg, setErrorMsg] = useState('');

  const withTimeout = (promise, ms) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
  ]);

  const messaggioBlocco = () => {
    if (window.__oneSignalInitError) {
      return `Servizio notifiche non attivo su questo indirizzo (${window.location.hostname}): su OneSignal il Site URL è "${window.__oneSignalSiteUrl || 'diverso'}". Apri l'app da quell'indirizzo oppure correggi il Site URL su OneSignal.`;
    }
    if (Notification.permission === 'denied') {
      return isIOS
        ? 'Notifiche bloccate da iOS. Vai in Impostazioni → Notifiche → Ristorantissimo e attiva "Consenti notifiche", poi riapri l\'app e riprova.'
        : 'Notifiche bloccate dal browser. Sbloccale dalle impostazioni del sito (icona lucchetto accanto all\'indirizzo) e riprova.';
    }
    return 'Il sistema non ha risposto. Chiudi completamente l\'app, riaprila dall\'icona e riprova con "Attiva Notifiche".';
  };

  const attivaConOneSignal = async () => {
    const OS = window.OneSignal;
    if (window.__oneSignalInitError) return false;
    // 1) permesso: prima via nativa (affidabile su iOS), poi OneSignal con timeout (mai spinner infinito)
    if (Notification.permission !== 'granted') {
      try { await withTimeout(Notification.requestPermission(), 20000); } catch (e) { console.warn('requestPermission nativa:', e.message); }
    }
    if (Notification.permission !== 'granted') return false;
    try { await withTimeout(OS.Notifications.requestPermission(), 12000); } catch (e) { console.warn('OneSignal requestPermission:', e.message); }
    // 2) iscrizione push OneSignal (se il permesso c'è ma l'iscrizione è disattiva)
    if (!OS.User?.PushSubscription?.optedIn) {
      try { await withTimeout(OS.User.PushSubscription.optIn(), 12000); } catch (e) { console.warn('OneSignal optIn:', e.message); }
    }
    if (tenantId) {
      try { await withTimeout(OS.User.addTag('tenant_id', tenantId), 8000); console.log('Tag tenant inviato:', tenantId); } catch (e) { console.warn('addTag:', e.message); }
    }
    console.log('Subscription ID:', OS.User?.PushSubscription?.id, '| optedIn:', OS.User?.PushSubscription?.optedIn);
    if (!OS.User?.PushSubscription?.id) {
      setErrorMsg('Permesso concesso ma iscrizione push non creata. Chiudi completamente l\'app, riaprila e riprova; su iPhone, se persiste, rimuovi l\'icona dalla Home e reinstallala.');
      return false;
    }
    return true;
  };

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      console.log('🔔 Tentativo attivazione notifiche...');
      
      // Verifica supporto
      if (!('Notification' in window)) {
        alert('Il tuo browser non supporta le notifiche');
        setIsLoading(false);
        setIsVisible(false);
        onClose?.();
        return;
      }
      
      // Usa OneSignal v16 API
      if (window.OneSignal && window.OneSignal.Notifications) {
        console.log('Uso OneSignal v16...');
        
        const granted = await attivaConOneSignal();
        if (!granted) {
          setErrorMsg(prev => prev || messaggioBlocco());
          return;
        }
        
        const subId = window.OneSignal.User?.PushSubscription?.id;
        
        setPermissionGranted(true);
        console.log('Notifiche attivate!');
        
        // Invia una notifica push di PROVA reale: verifica che arrivi anche ad app chiusa
        setTimeout(async () => {
          try {
            const freshSubId = window.OneSignal.User?.PushSubscription?.id || subId;
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifiche/test-push`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Tenant-DB': tenantId || '' },
              body: JSON.stringify({ subscription_id: freshSubId || null })
            });
            console.log('Notifica di prova inviata');
          } catch (e) {
            console.log('Errore invio notifica di prova:', e);
          }
        }, 2500);
        
        setTimeout(() => {
          setIsVisible(false);
          onClose?.();
        }, 4000);
        
      } else {
        // Fallback se OneSignal non caricato
        console.log('📲 OneSignal non disponibile, uso API nativa');
        const permission = await withTimeout(Notification.requestPermission(), 20000);
        if (permission === 'granted') {
          setPermissionGranted(true);
          new Notification('Notifiche Attivate!', {
            body: 'Riceverai avvisi per temperature e scadenze'
          });
          setTimeout(() => {
            setIsVisible(false);
            onClose?.();
          }, 1500);
        } else {
          setErrorMsg(messaggioBlocco());
        }
      }
      
    } catch (error) {
      console.error('❌ Errore attivazione notifiche:', error);
      setErrorMsg(messaggioBlocco());
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleNeverShow = () => {
    localStorage.setItem('notification_prompt_dismissed', 'true');
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) return null;

  // Su iOS non-PWA: mostra istruzioni per installare
  if (isIOS && !isStandalone) {
    return (
      <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header iOS */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Smartphone className="h-5 w-5" />
              <span className="font-semibold">Installa l'App</span>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Content iOS */}
          <div className="p-4">
            <p className="text-gray-700 text-sm mb-4">
              Per ricevere notifiche push su iPhone/iPad, installa l'app:
            </p>
            
            <div className="bg-gray-50 rounded-lg p-3 space-y-3 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-xs">1</span>
                </div>
                <p className="text-sm text-gray-700">
                  Tocca <Share className="inline h-4 w-4 text-blue-500 mx-1" /> 
                  <strong>Condividi</strong>
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-xs">2</span>
                </div>
                <p className="text-sm text-gray-700">
                  Tocca <strong>"Aggiungi a Home"</strong>
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-xs">3</span>
                </div>
                <p className="text-sm text-gray-700">
                  Tocca <strong>"Aggiungi"</strong>
                </p>
              </div>
            </div>
            
            <button
              onClick={handleNeverShow}
              className="w-full text-gray-500 text-sm py-1 hover:text-gray-700 transition-colors"
            >
              Non mostrare più
            </button>
          </div>
        </div>
      </div>
    );
  }

  // UI standard per Android/Desktop
  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-700 to-blue-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Bell className="h-5 w-5" />
            <span className="font-semibold">Attiva le Notifiche</span>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {permissionGranted ? (
            <div className="text-center py-2" data-testid="notifications-activated-message">
              <div className="text-green-600 font-semibold text-lg mb-1">
                ✅ Notifiche Attivate!
              </div>
              <p className="text-gray-500 text-sm">
                Ti abbiamo inviato una notifica di prova: se la ricevi, gli allarmi funzionano anche ad app chiusa 🔔
              </p>
              {isIOS && (
                <p data-testid="ios-kill-warning" className="text-amber-700 text-xs mt-2">
                  Su iPhone non chiudere l'app scorrendola via dal multitasking: le notifiche si fermano finché non la riapri.
                </p>
              )}
            </div>
          ) : (
            <>
              <p className="text-gray-700 text-sm mb-4">
                Ricevi notifiche push per:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 mb-4">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Compilazione automatica temperature
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-500">⚠</span>
                  Allarmi temperatura fuori range
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500">📦</span>
                  Lotti in scadenza
                </li>
              </ul>
              
              {errorMsg && (
                <div data-testid="notification-prompt-error" className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {errorMsg}
                </div>
              )}
              
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleEnableNotifications}
                  disabled={isLoading}
                  data-testid="enable-notifications-btn"
                  className="w-full bg-gradient-to-r from-cyan-700 to-blue-700 text-white py-2.5 px-4 rounded-lg font-medium hover:from-cyan-800 hover:to-blue-800 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Attivazione...
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4" />
                      Attiva Notifiche
                    </>
                  )}
                </button>
                <button
                  onClick={handleNeverShow}
                  className="w-full text-gray-500 text-sm py-1 hover:text-gray-700 transition-colors"
                >
                  Non mostrare più
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
